import { Environments, getEnvironmentFromHost } from '../api/endpoints';
import { CLUB_PHONE_NUMBERS } from '../shared/constants';
import jwt_decode from 'jwt-decode';
import { PaymentType } from '@ace-clublabs/payment-types';

export const getClubPhoneNumber = (clubCode: string) => {
  return CLUB_PHONE_NUMBERS[clubCode] || CLUB_PHONE_NUMBERS['004'];
};

export const getFooterContent = (clubCode: string) => {
  switch (clubCode) {
    case '004':
      return 'The Automobile Club of Southern California is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the following California counties: Inyo, Imperial, Kern, Los Angeles, Mono, Orange, Riverside, San Bernardino, San Diego, San Luis Obispo, Santa Barbara, Tulare, and Ventura.';
    case '252':
      return 'AAA Texas is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the state of Texas (with the exception of Texarkana).';
    case '601':
      return 'AAA New Mexico is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the state of New Mexico.';
    case '018':
      return "AAA Hawai'i is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the state of Hawai'i.";
    case '036':
      return 'AAA Northern New England is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in Maine, New Hampshire, and Vermont.';
    case '001':
      return 'AAA Alabama is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the state of Alabama.';
    case '215':
      return 'AAA East Central is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in Kentucky, New York, Ohio, Pennsylvania and West Virginia.';
    case '258':
      return "AAA Tidewater Virginia is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the Tidewater Virginia region (All Hampton Roads cities, including Norfolk, Chesapeake, Suffolk, Portsmouth, Virginia Beach, Hampton, Newport News, Williamsburg; Virginia's Eastern Shore; as well as the following counties: Greensville, Surry, Brunswick, Isle of Wight, Gloucester, Lancaster, Middlesex, Richmond County, Southampton, Sussex, Mecklenburg, James City County, York, Lunenburg, Essex, King & Queen, Mathews, Northumberland, and Westmoreland).";
    case '065':
      return 'The Automobile Club of Missouri is a member club affiliated with the American Automobile Association (AAA) national federation and serves members in the entire states of Arkansas, Louisiana, Mississippi and Missouri; the following counties in Kansas (Atchison, Brown, Doniphan, Johnson and Wyandotte); the following counties in Illinois (Alexander, Bond, Calhoun, Clinton, Edwards, Franklin, Gallatin, Greene, Hamilton, Hardin, Jackson, Jefferson, Jersey, Johnson, Madison, Marion, Massac, Monroe, Perry, Pope, Pulaski, Randolph, St. Clair, Saline, Union, Wabash, Washington, Wayne, White and Williamson); the following counties in Indiana (Crawford, Daviess, Dubois, Gibson, Knox, Martin, Perry, Pike, Posey, Spencer, Vanderburgh and Warrick); and the city of Texarkana, Texas.';
    default:
      return null;
  }
};

export const getFooterDisclaimer = (clubCode: string) => {
  const currentYear = new Date().getFullYear();
  switch (clubCode) {
    case '004':
      return `CST 1016202-80 Copyright © ${currentYear} Automobile Club of Southern California. All Rights Reserved. |`;
    case '252':
      return `Copyright © ${currentYear} AAA Texas, LLC. All Rights Reserved. |`;
    case '601':
      return `Copyright © ${currentYear} AAA New Mexico, LLC. All Rights Reserved. |`;
    case '018':
      return `Copyright © ${currentYear} AAA Hawai'i, LLC. All Rights Reserved. |`;
    case '036':
      return `Copyright © ${currentYear} AAA Northern New England. All Rights Reserved. |`;
    case '001':
      return `Copyright © ${currentYear} Alabama Motorists Association, Inc. All Rights Reserved. |`;
    case '215':
      return `Copyright © ${currentYear} AAA East Central. All Rights Reserved. |`;
    case '258':
      return `Copyright © ${currentYear} Tidewater Automobile Association of Virginia, Incorporated. All Rights Reserved. |`;
    case '065':
      return `Copyright © ${currentYear} Automobile Club of Missouri. All Rights Reserved. |`;
    default:
      return null;
  }
};

export const identityLoginUrl = () => {
  const env = getEnvironmentFromHost(window.location.hostname);
  let identityLoginUrl = '';
  switch (env) {
    case Environments.local:
    case Environments.localProxy:
    case Environments.dev:
      identityLoginUrl = 'https://appdev1.ace.aaa.com/enterprise/identity/dev/#/login';
      break;
    case Environments.qa:
      identityLoginUrl = 'https://appqa1.ace.aaa.com/enterprise/identity/qa/#/login';
      break;
    case Environments.uat:
      identityLoginUrl = 'https://appuat1.ace.aaa.com/enterprise/identity/uat/#/login';
      break;
    case Environments.prod:
      identityLoginUrl = 'https://app.ace.aaa.com/enterprise/identity/#/login';
      break;
  }
  return identityLoginUrl;
};

export const getIdentityEnv = () => {
  const env = getEnvironmentFromHost(window.location.hostname);
  let identityEnv = '';

  switch (env) {
    case Environments.local:
    case Environments.dev:
    case Environments.qa:
    case Environments.uat:
      identityEnv = 'non-prod';
      break;
    case Environments.prod:
      identityEnv = 'prod';
      break;
  }
  return identityEnv;
};

export const getIdentityRedirectUri = () => {
  const env = getEnvironmentFromHost(window.location.hostname);
  let identityRedirectUri = '';

  switch (env) {
    case Environments.local:
      identityRedirectUri = `https://localhost:8080/payments/manage-autopay`;
      break;
    case Environments.localProxy:
      identityRedirectUri = `https://autopay.app.ace.aaa.com:8080/payments/manage-autopay`;
      break;
    case Environments.dev:
      identityRedirectUri = `https://appdev1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.qa:
      identityRedirectUri = `https://appqa1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.uat:
      identityRedirectUri = `https://appuat1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.prod:
      identityRedirectUri = `https://app.ace.aaa.com/payments/manage-autopay`;
      break;
  }

  return identityRedirectUri;
};

export const getClubCode = (accessToken: string) => {
  const decoded: any = jwt_decode(accessToken);
  return decoded['clubCode'];
};

export const convertDateFormat = (inputDate: string) => {
  const [year, month, day] = inputDate.split('-');
  return `${month}/${day}/${year}`;
};

export const getPaymentMethodDescription = (paymentMethod?: PaymentType) => {
  switch (paymentMethod) {
    case PaymentType.CreditOrDebit:
      return 'Credit card';
    case PaymentType.DebitOnly:
      return 'Debit card';
    case PaymentType.Echeck:
      return 'E-check';
  }
};

export const navigateToManageAutoPay = () => {
  const env = getEnvironmentFromHost(window.location.hostname);
  let manageAutoPayUrl = '';

  switch (env) {
    case Environments.dev:
      manageAutoPayUrl = `https://appdev1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.qa:
      manageAutoPayUrl = `https://appqa1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.uat:
      manageAutoPayUrl = `https://appuat1.ace.aaa.com/payments/manage-autopay`;
      break;
    case Environments.prod:
      manageAutoPayUrl = `https://app.ace.aaa.com/payments/manage-autopay`;
      break;
    default:
      manageAutoPayUrl = `https://appuat1.ace.aaa.com/payments/manage-autopay`;
      break;
  }
  window.location.href = manageAutoPayUrl;
};
